
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { restaurants, categories } from '../../../mocks/foodDrink';

export default function FoodDrinkSection() {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredRestaurants = selectedCategory === 'all'
    ? restaurants
    : restaurants.filter(restaurant => {
        if (selectedCategory === 'fine-dining') return restaurant.category === 'Fine Dining';
        if (selectedCategory === 'casual') return restaurant.category === 'Casual Dining';
        if (selectedCategory === 'breweries') return restaurant.category === 'Brewery & Restaurant';
        return false;
      });

  return (
    <section id="food-drink" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Food & Drink Guide
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            From farm-to-fork fine dining to casual neighborhood favorites, explore Roseville's diverse culinary scene
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-6 py-2 rounded-full font-semibold transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                selectedCategory === category.id
                  ? 'bg-teal-500 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <i className={category.icon}></i>
              {category.name}
            </button>
          ))}
        </div>

        {/* Restaurants Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredRestaurants.map((restaurant) => (
            <div
              key={restaurant.id}
              className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 group"
            >
              <Link to={`/food-drink/${restaurant.id}`} className="cursor-pointer">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={restaurant.image}
                    alt={restaurant.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full">
                      {restaurant.cuisine}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <div className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full flex items-center gap-1">
                      <i className="ri-star-fill text-yellow-500 text-sm"></i>
                      <span className="text-sm font-semibold text-gray-900">{restaurant.rating}</span>
                    </div>
                  </div>
                </div>
              </Link>

              <div className="p-6">
                <div className="flex items-start justify-between mb-2">
                  <Link to={`/food-drink/${restaurant.id}`} className="cursor-pointer">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                      {restaurant.name}
                    </h3>
                  </Link>
                  <span className="text-teal-600 font-semibold text-lg">{restaurant.priceRange}</span>
                </div>

                <p className="text-sm text-gray-500 mb-3">{restaurant.category}</p>

                <p className="text-gray-600 mb-4 line-clamp-2">
                  {restaurant.description}
                </p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <i className="ri-map-pin-line text-teal-500"></i>
                    <span>{restaurant.address}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <i className="ri-time-line text-teal-500"></i>
                    <span>{restaurant.hours}</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Link
                    to={`/food-drink/${restaurant.id}`}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-900 rounded-lg font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap text-center cursor-pointer"
                  >
                    View Menu
                  </Link>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.name + ' ' + restaurant.address + ' Roseville CA')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-teal-500 text-white rounded-lg font-semibold hover:bg-teal-600 transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-navigation-line"></i>
                    Directions
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Link */}
        <div className="text-center mt-12">
          <Link 
            to="/food-drink"
            className="px-8 py-3 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer inline-block"
          >
            View All Restaurants
          </Link>
        </div>

        {/* Dining Stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-white rounded-xl">
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-restaurant-2-line text-teal-600 text-2xl"></i>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">300+</div>
            <div className="text-sm text-gray-600">Restaurants</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-white rounded-xl">
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-beer-line text-teal-600 text-2xl"></i>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">15+</div>
            <div className="text-sm text-gray-600">Craft Breweries</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-white rounded-xl">
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-cup-line text-teal-600 text-2xl"></i>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">50+</div>
            <div className="text-sm text-gray-600">Cafes & Coffee Shops</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-white rounded-xl">
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-goblet-line text-teal-600 text-2xl"></i>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">25+</div>
            <div className="text-sm text-gray-600">Wine & Cocktail Bars</div>
          </div>
        </div>
      </div>
    </section>
  );
}
